import { useState } from "react";
import { Search, MapPin, CheckCircle2, CircleDot, Circle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Overline, FadeIn, StatusBadge } from "@/components/site/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const milestoneIcon = (status) => {
  if (status === "completed") return <CheckCircle2 size={20} className="text-emerald-400" />;
  if (status === "in_progress") return <CircleDot size={20} className="text-ember" />;
  return <Circle size={20} className="text-silver/50" />;
};

export default function TrackProject() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(code.trim())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Tracking failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="track-project-page" className="min-h-[80vh]">
      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Track Project</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95]">
              Your Project.<br /><span className="text-ember">Live Status.</span>
            </h1>
            <p className="text-silver max-w-2xl mt-8 leading-relaxed">
              Enter the tracking code from your contract or quotation to see real-time construction status and milestone progress. Try <button type="button" onClick={() => setCode("OGK-COM-002")} className="text-ember font-bold hover:underline">OGK-COM-002</button> for a demo.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <form onSubmit={track} className="flex gap-3" data-testid="track-form">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. OGK-RES-001"
              data-testid="track-code-input" className="rounded-none bg-graphite border-line h-14 text-white uppercase tracking-widest" />
            <Button type="submit" disabled={loading} data-testid="track-submit-btn"
              className="rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-[0.15em] font-bold h-14 px-8">
              <Search size={16} className="mr-2" /> {loading ? "Tracking…" : "Track"}
            </Button>
          </form>

          {error && <p className="text-red-400 text-sm mt-6 border border-red-500/30 bg-red-500/10 px-5 py-4" data-testid="track-error">{error}</p>}

          {result && (
            <FadeIn className="mt-12">
              <div className="border border-line bg-graphite p-8 sm:p-10" data-testid="track-result">
                <div className="flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="text-silver text-xs uppercase tracking-[0.2em]">{result.tracking_code}</p>
                    <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white mt-2">{result.title}</h2>
                    <p className="text-silver text-sm mt-2 flex items-center gap-2"><MapPin size={14} className="text-ember" /> {result.location} · {result.category}</p>
                  </div>
                  <StatusBadge status={result.status} />
                </div>

                <div className="mt-10">
                  <div className="flex justify-between text-xs text-silver mb-2 uppercase tracking-wider">
                    <span>Overall Completion</span><span className="text-ember font-bold text-base">{result.completion_pct}%</span>
                  </div>
                  <div className="h-2 bg-line"><div className="h-2 bg-ember transition-[width] duration-700" style={{ width: `${result.completion_pct}%` }} /></div>
                </div>

                <h3 className="font-heading text-lg uppercase tracking-tight text-white mt-12 mb-6">Milestone Progress</h3>
                <div className="space-y-0 border-l border-line ml-2.5">
                  {result.milestones?.map((m, i) => (
                    <div key={i} className="relative pl-9 pb-8 last:pb-0">
                      <span className="absolute -left-[10px] top-0 bg-graphite">{milestoneIcon(m.status)}</span>
                      <p className={`text-sm font-medium ${m.status === "pending" ? "text-silver" : "text-white"}`}>{m.title}</p>
                      <p className="text-silver/70 text-xs mt-1 uppercase tracking-wider">{m.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}
